import { useState } from 'react'
import { isEmailValid } from 'src/common/helpers'
import { useNavigate } from 'react-router-dom'
import { FormikHelpers } from 'formik'
import { useAuth } from 'src/common/context'

interface Values {
  email: string
  password: string
}

interface State {
  error?: string
  success?: string
  setupMfa: boolean
  setupMfaParams: {
    secret: string
    email: string
  }
}

export const initialValues: Values = {
  email: '',
  password: ''
}

export const validate = (values: Values) => {
  const errors: Partial<Values> = {}

  if (!values.email) {
    errors.email = 'Required'
  } else if (!isEmailValid(values.email)) {
    errors.email = 'Invalid email address'
  }
  if (!values.password) {
    errors.password = 'Required'
  }

  return errors
}

const useLogin = () => {
  const navigate = useNavigate()
  const mfaPath = { pathname: '/mfa' }

  const { login } = useAuth()

  const [state, setState] = useState<State>({
    error: undefined,
    success: undefined,
    setupMfa: false,
    setupMfaParams: {
      email: '',
      secret: ''
    }
  })

  const onSubmit = async (values: Values, { setSubmitting, resetForm }: FormikHelpers<Values>) => {
    const { email, password } = values

    try {
      const signInResult = await login({
        username: email.toLowerCase(),
        password
      })
      resetForm()
      setSubmitting(false)
      const signInStep = signInResult.nextStep.signInStep
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sharedSecret = (signInResult.nextStep as any).totpSetupDetails?.sharedSecret
      if (signInStep === 'RESET_PASSWORD') {
        // Navigate to reset password page - it will handle completing the new password challenge
        navigate(
          { pathname: '/reset-password', search: '?newPasswordRequired=true' },
          {
            replace: true,
            state: {
              email: email.toLowerCase()
            }
          }
        )
        return
      }
      if (signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        navigate(mfaPath, {
          replace: true,
          state: {
            email
          }
        })
      } else if (signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        const setupMfaParams = { email: email.toLowerCase(), secret: sharedSecret }
        setState({ ...state, setupMfa: true, setupMfaParams })
      } else if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        navigate(
          { pathname: '/reset-password', search: '?newPasswordRequired=true' },
          {
            replace: true,
            state: {
              email: email.toLowerCase()
            }
          }
        )
        return
      }
    } catch (e) {
      setState({
        error: (e as Error).message,
        success: undefined,
        setupMfa: false,
        setupMfaParams: {
          email: '',
          secret: ''
        }
      })
    }
  }

  return { state, setState, onSubmit }
}

export default useLogin
